import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Modal,
  IconButton,
} from "@mui/material";
import { useToast } from "@chakra-ui/react";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { CloseOutlined } from "@mui/icons-material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import useBulkImportPreview from "../../hooks/useBulkImportPreview";
import BulkImportPreviewTable from "./BulkImportPreviewTable";

const INSTRUMENT_IMPORT_TEMPLATE_FILENAME = "instrument_bulk_upload_template.xlsx";
const ROW_FIELDS = [
  "tagNumber",
  "instrumentType",
  "ioPanel",
  "channel",
  "card",
  "plc",
  "pid",
  "communicationProtocol",
  "description",
];

const COLUMNS = [
  { key: "tagNumber", label: "Tag Number" },
  { key: "instrumentType", label: "Instrument Type" },
  { key: "ioPanel", label: "IO Panel" },
  { key: "channel", label: "Channel" },
  { key: "card", label: "Card" },
  { key: "plc", label: "PLC" },
  { key: "pid", label: "P&ID" },
  { key: "communicationProtocol", label: "Communication Protocol" },
  { key: "description", label: "Description" },
];

function InstrumentCsvUploadFormContent({
  closeModal,
  reloadInstruments,
  siteId,
  plantId,
  assetId,
  tierId,
  componentId,
  onPreviewChange,
}) {
  const toast = useToast();

  const scopeLabel = useMemo(() => {
    const parts = ["Site", "Plant"];
    if (assetId) parts.push("Equipment");
    if (tierId) parts.push("Tier");
    if (componentId) parts.push("Component");
    return parts.join(" > ");
  }, [assetId, componentId, tierId]);

  const fieldControls = {
    instrumentType: {
      type: "lookup-create",
      optionsEndpoint: "/instrument-types/",
      createEndpoint: "/instrument-types/",
    },
    communicationProtocol: {
      type: "lookup-create",
      optionsEndpoint: "/communication-types/",
      createEndpoint: "/communication-types/",
    },
    ioPanel: {
      type: "lookup-search",
      optionsEndpoint: "/tiers/",
      optionsParams: { plant: plantId },
      getOptionLabel: (option) =>
        [option.tagNumber, option.tierName].filter(Boolean).join(" - "),
      getValue: (option) => option.tagNumber,
    },
    plc: {
      type: "lookup-search",
      optionsEndpoint: "/equipment/",
      optionsParams: { plant: plantId },
      getOptionLabel: (option) =>
        [option.tagNumber, option.equipmentName].filter(Boolean).join(" - "),
      getValue: (option) => option.tagNumber,
    },
    pid: {
      type: "lookup-search",
      optionsEndpoint: "/equipment/",
      optionsParams: { plant: plantId },
      getOptionLabel: (option) =>
        [option.tagNumber, option.equipmentName].filter(Boolean).join(" - "),
      getValue: (option) => option.tagNumber,
    },
  };

  const extraContext = useMemo(() => {
    const context = { site_id: siteId, plant_id: plantId };
    if (assetId) context.equipment_id = assetId;
    if (tierId) context.tier_id = tierId;
    if (componentId) context.component_id = componentId;
    return context;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, plantId, assetId, tierId, componentId]);

  const {
    file,
    preview,
    previewing,
    revalidating,
    uploading,
    downloadingTemplate,
    error,
    rowErrors,
    handleFileChange,
    handleDownloadTemplate,
    handleRowFieldEdit,
    handleRemoveRow,
    handleSubmit,
  } = useBulkImportPreview({
    importExcelUrl: "/instruments/import-excel/",
    importRowsUrl: "/instruments/import-rows/",
    templateUrl: "/instruments/import-template/",
    templateFilename: INSTRUMENT_IMPORT_TEMPLATE_FILENAME,
    rowFields: ROW_FIELDS,
    extraContext,
  });

  useEffect(() => {
    onPreviewChange(Boolean(preview));
  }, [preview, onPreviewChange]);

  const onDownloadTemplate = async () => {
    if (!siteId || !plantId) return;
    const result = await handleDownloadTemplate();
    if (!result.ok) {
      toast({
        title: "Template download failed",
        description: result.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const onSubmit = async () => {
    const result = await handleSubmit();
    if (result.ok) {
      toast({
        title: "Upload complete",
        description: "Import completed successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      await reloadInstruments?.();
      closeModal();
    } else {
      toast({
        title: "Upload failed",
        description: result.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "text.secondary", mb: 1.5 }}>
          Upload scope: {scopeLabel}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadOutlined />}
          >
            Choose File
            <input
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
            />
          </Button>

          <Button
            variant="outlined"
            onClick={onDownloadTemplate}
            disabled={downloadingTemplate}
            startIcon={
              downloadingTemplate ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadOutlined />
              )
            }
          >
            Download Template
          </Button>
        </Box>

        {file && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
            <Typography sx={{ fontSize: "14px" }}>
              Selected file: <strong>{file.name}</strong>
            </Typography>
            {previewing && (
              <>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  Checking file…
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preview && (
        <Box
          sx={{
            p: 2,
            borderRadius: "10px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Preview
            </Typography>
            {revalidating && (
              <>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  Re-checking…
                </Typography>
              </>
            )}
          </Box>

          <BulkImportPreviewTable
            preview={preview}
            columns={COLUMNS}
            fieldControls={fieldControls}
            onFieldEdit={handleRowFieldEdit}
            onRemoveRow={handleRemoveRow}
            revalidating={revalidating}
            uploading={uploading}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={closeModal}
          disabled={uploading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          fullWidth
          onClick={onSubmit}
          disabled={
            uploading ||
            previewing ||
            revalidating ||
            !file ||
            !preview ||
            rowErrors.length > 0
          }
        >
          {uploading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Upload"
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default function InstrumentCsvUpload({
  reloadInstruments,
  siteId,
  plantId,
  assetId,
  tierId,
  componentId,
}) {
  const [open, setOpen] = useState(false);
  const [hasPreview, setHasPreview] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setHasPreview(false);
  };

  return (
    <>
      <span onClick={() => setOpen(true)}><FormatListBulletedIcon fontSize="small" /> Upload Instrument Excel</span>

      <Modal open={open} onClose={handleClose} sx={{ zIndex: 1000 }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: hasPreview ? "94%" : 560 },
            maxWidth: hasPreview ? 1600 : 560,
            transition: "width 0.2s ease, max-width 0.2s ease",
            bgcolor: "background.paper",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
              >
                Upload Instrument CSV / Excel
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                Accepts .csv, .xlsx, and .xls files
              </Typography>
            </Box>

            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, py: 2.5, overflowY: "auto" }}>
            {open && (
              <InstrumentCsvUploadFormContent
                closeModal={handleClose}
                reloadInstruments={reloadInstruments}
                siteId={siteId}
                plantId={plantId}
                assetId={assetId}
                tierId={tierId}
                componentId={componentId}
                onPreviewChange={setHasPreview}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
}
